import type { SideNavItem, SideNavSection } from '@billda/design-system';
import { Features } from '@billda/features-ui';
import {
  topNavItems,
  primaryNavItems,
  platformNavItems,
  secondaryNavItems,
  type NavigationItem,
} from '../navigationItems';

export interface BuildSidebarSectionsInput {
  permissions: string[];
  isActive: (path: string) => boolean;
  onNavigate: (item: NavigationItem) => void;
  isDashboardEnabled: boolean;
  isWorkActivityEnabled: boolean;
  isEnhancedFilesEnabled: boolean;
  /** Getting Started is hidden once the checklist is completed. */
  gettingStartedCompleted: boolean;
}

/**
 * Maps navigationItems.ts onto the DS SideNav model with the same guards the
 * old SidebarContent/SidebarNavItem/SidebarNavGroup applied:
 * - leaf items are visible when their permission and feature flag both pass
 * - groups are visible when the group itself passes AND at least one sub-item
 *   passes the permission guard (feature-flag filtering stays per rendered
 *   sub-item, exactly like before)
 *
 * Note: the "Dashboard — Soon" teaser keeps its portal behaviour (rendered
 * only while the Dashboard feature flag is off, not clickable) — the DS
 * SideNavItem `trailing` slot carries the "Soon" caption.
 */
export function buildSidebarSections(
  input: BuildSidebarSectionsInput
): SideNavSection[] {
  const {
    permissions,
    isActive,
    onNavigate,
    isDashboardEnabled,
    isWorkActivityEnabled,
    isEnhancedFilesEnabled,
    gettingStartedCompleted,
  } = input;

  // Same contract as useNavItemVisible — feature flags consulted per item.
  // Flags not in this fixed set default to enabled.
  const featureEnabled: Record<string, boolean> = {
    [Features.Dashboard.IsEnabled]: isDashboardEnabled,
    [Features.WorkActivity.IsEnabled]: isWorkActivityEnabled,
    [Features.Files.IsEnhancedFileManagerEnabled]: isEnhancedFilesEnabled,
  };

  const isVisible = (item: NavigationItem): boolean => {
    if (item.featureFlag && !(featureEnabled[item.featureFlag] ?? true)) {
      return false;
    }
    if (item.permission) {
      return permissions.includes(item.permission);
    }
    return true;
  };

  const toLeaf = (item: NavigationItem): SideNavItem => {
    return {
      key: item.text,
      text: item.text,
      icon: item.icon,
      active: isActive(item.path),
      highlight: item.highlight,
      onClick: () => {
        onNavigate(item);
      },
    };
  };

  const toItemOrGroup = (item: NavigationItem): SideNavItem | null => {
    if (!item.subItems?.length) {
      return isVisible(item) ? toLeaf(item) : null;
    }
    if (!isVisible(item)) {
      return null;
    }
    // Hide the group when no sub-item passes the permission guard.
    const permissionVisibleSubItems = item.subItems.filter(sub => {
      return sub.permission ? permissions.includes(sub.permission) : true;
    });
    if (!permissionVisibleSubItems.length) {
      return null;
    }
    return {
      key: item.text,
      text: item.text,
      items: item.subItems.filter(isVisible).map(toLeaf),
    };
  };

  const topItems = gettingStartedCompleted
    ? topNavItems.filter(item => item.text !== 'Getting Started')
    : topNavItems;

  const dashboardTeaser = topNavItems.find(item => item.text === 'Dashboard');

  const mainItems = [
    // Dashboard teaser while the feature flag is off (no onClick — not a route).
    ...(!isDashboardEnabled && dashboardTeaser
      ? [
          {
            key: 'dashboard-soon',
            text: dashboardTeaser.text,
            icon: dashboardTeaser.icon,
            trailing: 'Soon',
            testId: 'expanded-nav-dashboard',
          },
        ]
      : []),
    ...topItems.map(toItemOrGroup),
    ...platformNavItems.map(toItemOrGroup),
    ...primaryNavItems.map(toItemOrGroup),
  ].filter((item): item is SideNavItem => {
    return item !== null;
  });

  const secondaryItems = secondaryNavItems
    .map(toItemOrGroup)
    .filter((item): item is SideNavItem => {
      return item !== null;
    });

  return [
    { key: 'primary', items: mainItems },
    { key: 'secondary', items: secondaryItems, placement: 'footer' },
  ];
}
